/* eslint-disable no-nested-ternary */
import { ReactElement, useState, useContext, useRef, useEffect } from 'react';
import { Field, useFormikContext, getIn, ErrorMessage } from 'formik';
import Autosuggest from 'react-autosuggest';
import { Button, DropdownMenu, TextInput, Alert, Modal } from '@components';
import { iSuggestion, iCountry } from '@interfaces/interfaces';
import { AusStateList } from '@constants';
import { iThirdPartyFormValues } from '@services/ThirdPartyService';
import { iCourtFormValues } from '@services/CourtService';
import { iFVSFormValues } from '@services/FVSService';
import VicFinesFormContext from '@pages/forms/VicFinesFormContext';
import gridFormStyles from '@pages/forms/Grid.module.scss';
import classnames from 'classnames';
import { autocompleteAddress, captureAddress } from '@api/Address';
import AddressFieldAlertContent from '../Content/AlertContent/AddressFieldAlertContent';
import InfoIcon from '../../images/info.svg';
import { ReactComponent as SearchIcon } from '../../images/search.svg';
import styles from './AddressField.module.scss';
import theme from './Autosuggest.theme.module.scss';

interface iProps {
  name: string;
  title: string;
  manualAddressText: string;
  movedRecentlyText?: string;
  isMandatory: boolean;
  disabled?: boolean;
}

const AddressField = ({ name, title, manualAddressText, isMandatory, disabled = false, movedRecentlyText = '' }: iProps): ReactElement => {
  const { values, errors, touched }: { values: iThirdPartyFormValues | iCourtFormValues | iFVSFormValues; errors: any; touched: any } = useFormikContext();
  const refAutoSuggest = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [manualAddress, setManualAddress] = useState(!!getIn(values, `${name}.LineOne`));
  const [addressFound, setAddressFound] = useState(false);
  const [isAddressFieldVisible, setAddressFieldVisible] = useState(!(getIn(values, `${name}.LineOne`)
  && getIn(values, `${name}.Suburb`)
  && getIn(values, `${name}.Postcode`)
  && getIn(values, `${name}.State`)
  && getIn(values, `${name}.Country`)
));
  const [isOpenInfo, setOpenInfo] = useState(false);
  const formikProps = useFormikContext();
  const { countryList } = useContext(VicFinesFormContext);

  const closeNotifications = () => {
    setOpenInfo(false);
  };

  const addressNotificationTitle = (): ReactElement => (
    <div className={styles.modalTitle}>
      <span>Change of address? Remember to update your details with </span>
      <a href="https://www.vicroads.vic.gov.au/licences/renew-replace-or-update/update-your-details/change-of-address" target="_blank" rel="noreferrer">
        VicRoads
      </a>
    </div>
  );

  const addressNotification = (): ReactElement => (
    <>
      <div className={styles.sectionTitle}>Have you moved recently?</div>
      <div className={styles.sectionDescription}>
        <p>
          By law you must advise VicRoads of your new address within 14 days of moving. Fines sent to the address registered with VicRoads are 'deemed served', even if you no
          longer liver there and did not receive them. This means that agencies can take enforcement action against you if you have not dealt with a fine sent to an old address.
          You can update both your licence and vehicle registration details online at VicRoads.
        </p>
      </div>
      <div className={styles.btnFooter}>
        <Button onClick={closeNotifications} color="blue" size="small">
          Continue
        </Button>
      </div>
    </>
  );

  const COUNTRY_DROPDOWN = countryList.map((item: iCountry): { value: string; display: string } => ({
    value: item.Name,
    display: item.Name,
  }));

  const setDefaultAddressValues = () => {
    formikProps.setFieldValue(name, {
      SearchText: '',
      Address: '',
      LineOne: '',
      LineTwo: '',
      Suburb: '',
      State: '',
      Postcode: '',
      Country: 'Australia',
    });
  };

  const setAddressFieldsTouched = (areFieldsTouched: boolean) => {
    formikProps.setFieldTouched(`${name}.LineOne`, areFieldsTouched);
    formikProps.setFieldTouched(`${name}.LineTwo`, areFieldsTouched);
    formikProps.setFieldTouched(`${name}.Suburb`, areFieldsTouched);
    formikProps.setFieldTouched(`${name}.State`, areFieldsTouched);
    formikProps.setFieldTouched(`${name}.Postcode`, areFieldsTouched);
    formikProps.setFieldTouched(`${name}.Country`, areFieldsTouched);
  };

  const getSuggestions = async (queryString: string) => {
    const json = await autocompleteAddress(queryString);
    setSuggestions(json.results);
  };

  const getAddressFormat = async (url: string) => {
    const json = await captureAddress(url);
    const format = json.address;
    const { country } = format[6];
    const formattedCountry = country
      .split(' ')
      .map((word: string) => word.charAt(0) + word.substring(1).toLowerCase())
      .join(' ');
    formikProps.setFieldValue(`${name}.LineOne`, format[0].addressLine1);
    formikProps.setFieldValue(`${name}.LineTwo`, format[1].addressLine2);
    formikProps.setFieldValue(`${name}.Suburb`, format[3].locality);
    formikProps.setFieldValue(`${name}.State`, format[4].province);
    formikProps.setFieldValue(`${name}.Postcode`, format[5].postalCode);
    formikProps.setFieldValue(`${name}.Country`, formattedCountry);
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    getSuggestions(value);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestionValue = (suggestion: iSuggestion): string => {
    const suggestionValue = suggestion.suggestion;
    if (suggestionValue) return suggestionValue;
    return '';
  };

  const renderSuggestion = (suggestion: iSuggestion): ReactElement => {
    const suggestionValue = suggestion.suggestion;
    if (suggestionValue) {
      return <div>{suggestion.suggestion}</div>;
    }
    return <></>;
  };

  const onSuggestionSelected = (event: any, { suggestion }: { suggestion: iSuggestion }) => {
    const suggestionValue = suggestion.suggestion;
    if (suggestionValue) {
      formikProps.setFieldValue(`${name}.Address`, suggestion.suggestion);
      const url = suggestion.format;
      getAddressFormat(url);
    }
    setManualAddress(true);
    setAddressFieldVisible(false);
    setSearchText('');
    setAddressFound(true);
  };

  const inputProps = {
    value: searchText,
    onChange: (event: any, { newValue }: { newValue: string }) => {
      setSearchText(newValue);
      formikProps.setFieldValue(`${name}.SearchText`, newValue);
    },
    onBlur: () => {
      formikProps.setFieldTouched(`${name}.SearchText`);
    },
    disabled: disabled || manualAddress,
    placeholder: manualAddress && !addressFound
      ? 'My address is not listed'
      : `Search for ${
          title.toLowerCase().includes('residential address') ? 'residential address' : title.toLowerCase().includes('postal address') ? 'postal address' : title.toLowerCase()
        }`,
  };

  const countryChange = () => {
    formikProps.setFieldValue(`${name}.State`, '');
  };
  const hasTouchedAddressFields = getIn(touched, `${name}.LineOne`)
    || getIn(touched, `${name}.LineTwo`)
    || getIn(touched, `${name}.Suburb`)
    || getIn(touched, `${name}.Postcode`)
    || getIn(touched, `${name}.State`)
    || getIn(touched, `${name}.Country`);
  const hasAdressFieldsErrors = (
    getIn(errors, `${name}.LineOne`)
    || getIn(errors, `${name}.LineTwo`)
    || getIn(errors, `${name}.Suburb`)
    || getIn(errors, `${name}.Postcode`)
    || getIn(errors, `${name}.State`)
    || getIn(errors, `${name}.Country`)
  ) && !manualAddress;

  useEffect(() => {
    if (disabled) setAddressFieldVisible(true);
    if (getIn(values, `${name}.SearchText`) === '') {
      setSearchText('');
    }
    setManualAddress(getIn(values, `${name}.LineOne`) || getIn(values, `${name}.Suburb`) || getIn(values, `${name}.State`) || getIn(values, `${name}.Postcode`));
  }, [getIn(values, `${name}.LineOne`), getIn(values, `${name}.Suburb`), getIn(values, `${name}.State`), getIn(values, `${name}.Postcode`), getIn(values, `${name}.SearchText`), disabled]);

  return (
    <div className={styles.container}>
      {title && (
        <div>
          <span
            className={classnames({
              [styles.title]: true,
              [styles.titleError]: hasAdressFieldsErrors && hasTouchedAddressFields,
            })}>
            {title}
          </span>
          {!isMandatory && <span className={styles.optional}>( optional )</span>}
        </div>
      )}
      {isAddressFieldVisible && (
        <div className={styles.autosuggestContainer} id={name} ref={refAutoSuggest} tabIndex={-1}>
          <div className={styles.autosuggest}>
            <SearchIcon className={styles.searchIcon} />
            <Autosuggest
              suggestions={suggestions}
              onSuggestionsFetchRequested={onSuggestionsFetchRequested}
              onSuggestionsClearRequested={onSuggestionsClearRequested}
              getSuggestionValue={getSuggestionValue}
              renderSuggestion={renderSuggestion}
              onSuggestionSelected={onSuggestionSelected}
              inputProps={inputProps}
              theme={theme}
            />
          </div>
        </div>
      )}
      {hasAdressFieldsErrors && hasTouchedAddressFields && isAddressFieldVisible && <div className={styles.errorMsg}>This field is required</div>}
      {manualAddress ? (
        <div className={styles.manualInputForm}>
          {!addressFound && isAddressFieldVisible && <Alert message={<AddressFieldAlertContent title={title} />} icon={InfoIcon} theme="info2" />}
          <div className={`${gridFormStyles.form}`}>
            <div className={gridFormStyles.gridColFull}>
              <Field name={`${name}.LineOne`} component={TextInput} placeholder="Street Address, P.O. Box, company name, c/o" isMandatory />
            </div>
            <div className={gridFormStyles.gridColFull}>
              <Field name={`${name}.LineTwo`} component={TextInput} placeholder="Apt, Suite, Unit, Building, Floor" />
            </div>
            <div className={gridFormStyles.gridColHalf}>
              <Field name={`${name}.Postcode`} component={TextInput} title="Postal code" isMandatory />
              <Field name={`${name}.Suburb`} component={TextInput} title="Suburb" isMandatory />
            </div>
            <div className={gridFormStyles.gridColHalf}>
              <Field name={`${name}.Country`} component={DropdownMenu} title="Country" displayText={COUNTRY_DROPDOWN} isMandatory changeOption={countryChange} />
              {getIn(values, `${name}.Country`) === 'Australia' ? (
                <Field name={`${name}.State`} component={DropdownMenu} title="State/Province/Region" displayText={AusStateList} isMandatory />
              ) : (
                <Field name={`${name}.State`} component={TextInput} title="State/Province/Region" isMandatory />
              )}
            </div>
          </div>
          <div>
            <button
              type="button"
              className={styles.addressButton}
              onClick={async () => {
                setManualAddress(false);
                setAddressFieldVisible(true);
                setDefaultAddressValues();
                setAddressFieldsTouched(false);
                formikProps.setFieldValue(`${name}.InternationalAddress`, false);
                refAutoSuggest.current?.focus();
              }}>
              <u>I want to search for my address again</u>
            </button>
          </div>
        </div>
      ) : (
        <div>
          <ErrorMessage className={styles.errorMsg} component="div" name={`${name}.SearchText`} />
          <button
            type="button"
            className={styles.addressButton}
            disabled={disabled}
            onClick={() => {
              setSearchText('');
              setManualAddress(true);
              setAddressFound(false);
            }}>
            {manualAddressText}
          </button>
          <br />
        </div>
      )}
      {movedRecentlyText !== '' && (
        <>
          <div className={styles.movedRecentlyButton}>
            <button type="button" className={styles.addressButton} disabled={disabled} onClick={() => setOpenInfo(true)}>
              {movedRecentlyText}
            </button>
            <br />
          </div>
        </>
      )}
      {isOpenInfo && <Modal close={closeNotifications} show title={addressNotificationTitle()} content={addressNotification()} />}
    </div>
  );
};

export default AddressField;
